import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Grid,
    Paper,
    ListItem,
    IconButton,
    Typography,
} from '@material-ui/core';
import { Table } from 'rsuite';
import {
    Edit as EditIcon,
} from '@material-ui/icons';
// eslint-disable-next-line import/no-unresolved
import { applyUkgUserTimeZoneTransformation } from '@commonClient2/services/ukgDateTime';
import { UkgDateFormat } from '../../../Enums';
import { fetchBidSelectionTemplateAppData } from '../../store/appData';
import DateFormatService from '../../../services/DateFormatService';
import { selectBidSelectionTemplate } from '../../store/bidSelectionTemplate';
import getSortIcon from '../SortIcon';
import './TemplateSelection.scss';

const { Column, HeaderCell, Cell } = Table;

const widthMobileToggle = 993;
/**
 * @param {{search: string}} props
 */
function TemplateSelection(props) {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'employee.bidSelectionTemplate' });
    const dispatch = useDispatch();

    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const { employeeAppData } = useSelector((state) => state);
    const { selectionTemplates } = employeeAppData;

    const [filteredTemplates, setFilteredTemplates] = useState(selectionTemplates);

    const { search } = props;

    const [windowWidth, setWindowWidth] = useState(0);
    useEffect(() => {
        function handleResize() {
            setWindowWidth(window.innerWidth);
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const isMobile = windowWidth < widthMobileToggle;

    useEffect(() => {
        dispatch(fetchBidSelectionTemplateAppData());
    }, []);

    useEffect(() => {
        if (search) {
            const filtered = selectionTemplates.filter((x) => {
                const searchString = `${x.name}
                    |${DateFormatService.formatDateOrEmpty(x.creationDate, UkgDateFormat.LongDateTime)}
                    |${DateFormatService.formatDateOrEmpty(x.lastUpdate, UkgDateFormat.LongDateTime)}`
                    .toLocaleLowerCase();
                return searchString.includes(search.toLocaleLowerCase());
            });
            setFilteredTemplates(filtered);
        } else {
            setFilteredTemplates(selectionTemplates);
        }
    }, [JSON.stringify(selectionTemplates), search]);

    /**
     * @param {EmployeeBidSelectionTemplate.IEmployeeBidSelectionTemplateListItem} rowData
     */
    const handleEdit = (rowData) => {
        dispatch(selectBidSelectionTemplate(rowData.profileId));
    };

    const renderDesktopView = () => (
        <Grid
            container
            justifyContent="space-between"
            className="template-grid-container"
        >
            <Grid
                item
                xs={12}
                lg={12}
            >
                <Paper elevation={2} className="template-selection-table-container">
                    <Table
                        key="employee-bid-template-table"
                        data={filteredTemplates}
                        autoHeight
                        id="employee-bid-template-table"
                        rowClassName="employee-bid-template-table-row"
                    >
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell renderSortIcon={(sortType) => (getSortIcon(sortType))}>
                                {t('tableHeaders.name')}
                            </HeaderCell>
                            <Cell dataKey="name" />
                        </Column>
                        <Column
                            flexGrow={1}
                            sortable
                        >
                            <HeaderCell renderSortIcon={(sortType) => (getSortIcon(sortType))}>
                                {t('tableHeaders.creationDate')}
                            </HeaderCell>
                            <Cell
                                dataKey="Start Date"
                                style={{ cursor: 'default' }}
                            >
                                {(/** @type {EmployeeBidSelectionTemplate.IEmployeeBidSelectionTemplateListItem} */ rowData) =>
                                    // eslint-disable-next-line implicit-arrow-linebreak
                                    DateFormatService.formatDateOrEmpty(
                                        rowData.creationDate,
                                        UkgDateFormat.LongDateTime,
                                    )}
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={1}
                            sortable
                        >
                            <HeaderCell renderSortIcon={(sortType) => (getSortIcon(sortType))}>
                                {t('tableHeaders.lastUpdate')}
                            </HeaderCell>
                            <Cell
                                dataKey="Last update"
                                style={{ cursor: 'default' }}
                            >
                                {(/** @type {EmployeeBidSelectionTemplate.IEmployeeBidSelectionTemplateListItem} */ rowData) =>
                                    // eslint-disable-next-line implicit-arrow-linebreak
                                    DateFormatService.formatDateOrEmpty(
                                        rowData.lastUpdate,
                                        UkgDateFormat.LongDateTime,
                                    )}
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={1}
                            fixed="right"
                        >
                            <HeaderCell> </HeaderCell>
                            <Cell>
                                {(/** @type {EmployeeBidSelectionTemplate.IEmployeeBidSelectionTemplateListItem} */rowData) => (
                                    <div
                                        className="employee-bid-template-action-button-container"
                                    >
                                        <Button
                                            onClick={() => handleEdit(rowData)}
                                            className="employee-bid-template-tablerow-action-button"
                                        >
                                            
                                            <EditIcon fontSize="inherit" />
                                            <div className="edit-button-label">Edit</div>
                                        </Button>
                                    </div>
                                )}
                            </Cell>
                        </Column>
                    </Table>
                </Paper>
            </Grid>
        </Grid>
    );

    const renderMobileView = () => filteredTemplates.map((x) => (
        <ListItem
            key={`ListItem_Scheduled_bidProfileItem_${x.profileId}`}
        >
            <IconButton
                className="ts-card-button-root"
                onClick={() => handleEdit(x)}
            >
                <Paper className="ts-card-paper">
                    <Grid
                        container
                        xs={12}
                        className="ts-card-header-container"
                    >
                        <Grid
                            item
                            xs={12}
                            className="ts-card-header-container"
                        >
                            <Typography
                                variant="h6"
                                className="ts-card-header-title"
                            >
                                {x.name}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            className="ts-card-header-container"
                        >
                            <Typography
                                variant="body2"
                                gutterBottom
                            >
                                {`${t('tableHeaders.lastUpdate')} :`}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={6}
                        >
                            <Typography
                                variant="body2"
                                className="ts-card-header-subtitle"
                                gutterBottom
                            >
                                {
                                    DateFormatService.formatDateOrEmpty(
                                        applyUkgUserTimeZoneTransformation(x.lastUpdate),
                                        UkgDateFormat.LongDateTime,
                                    )
                                }
                            </Typography>
                        </Grid>

                        <Grid
                            item
                            xs={6}
                            className="ts-card-header-container"
                        >
                            <Typography
                                variant="body2"
                                gutterBottom
                            >
                                {`${t('tableHeaders.creationDate')} :`}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={6}
                        >
                            <Typography
                                variant="body2"
                                className="ts-card-header-subtitle"
                                gutterBottom
                            >
                                {
                                    DateFormatService.formatDateOrEmpty(
                                        applyUkgUserTimeZoneTransformation(x.creationDate),
                                        UkgDateFormat.LongDateTime,
                                    )
                                }
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </IconButton>

        </ListItem>
    ));

    return (
        <div className="employee-full-page">
            {
                isMobile ? renderMobileView() : renderDesktopView()
            }
        </div>
    );
}

export default TemplateSelection;
